export const MODULE_NAME = '@pressreader/article';

/**
 * Host-config key gating the "Export to Zotero" article-tool button. Read through
 * `selectArticleFeaturesConfig` and defaults to `false`, so a host that has not opted in never shows it.
 *
 * The backend node is `params/FeatureFlags/views.article.zotero.enabled` — **without** the `features.`
 * prefix, which the config layer adds. Provisioning it as `features.views…` yields `features.features.views…`
 * and the flag reads as missing.
 */
export const ZOTERO_EXPORT_FEATURE_FLAG = 'features.views.article.zotero.enabled';

/**
 * Host-config key gating the copyright notice prepended to article text the reader copies.
 * Read through `selectArticleFeaturesConfig` and defaults to `false`, so a host that has not opted
 * in copies exactly as it does today — the `copy` listener is never installed and the dialog renders
 * no notice.
 *
 * The backend node is `params/FeatureFlags/views.article.copyLegalNotice.enabled` — **without** the
 * `features.` prefix, which the config layer adds. Provisioning it as `features.views…` yields
 * `features.features.views…` and the flag reads as missing.
 */
export const COPY_LEGAL_NOTICE_FEATURE_FLAG = 'features.views.article.copyLegalNotice.enabled';

/** Resource key holding the notice wording. Resolved per host, so the text lives in the backend. */
export const COPY_LEGAL_NOTICE_RESOURCE_KEY = 'Dialogs.Copy.LegalNotice';

/** Class on the notice the copy dialog renders, so the interceptor can drop it before prepending. */
export const COPY_LEGAL_NOTICE_CLASS = 'copy-legal-notice';

/**
 * Marks an article subtree as copyable content, so the copy interceptor can tell it apart from a
 * feed teaser.
 *
 * Set on the outer `<article class="article expanded">` of each reading surface, which matches the
 * scope the copy dialog already uses: `.copy-snippet` wraps the title, the byline and the body, so a
 * headline copied from the page is treated the same as one copied from the dialog.
 *
 * Five elements carry `article expanded`. Four are reading surfaces and are tagged: MultiColumnArticle
 * (ui-common/controls/article.tsx), the desktop renderer (ui-desktop/content/article.tsx), the
 * facelift single column (ui-facelift/facelift/single.column.article.container.tsx) and the mobile
 * renderer (ui-facelift/mobile/content.tsx), which pressreader reaches at phone size and BE renders
 * directly. The fifth, `ui-common/controls/blocks/etalon.line.tsx`, is left untagged on purpose: it
 * is an off-screen dummy-text node used to measure line height, so nothing there is selectable.
 *
 * An attribute rather than a class because no class name discriminates: `.article-content` is
 * rendered by feed cards as well as the article body, and `.article-wrapper` is shared by feed cards
 * and the article renderers.
 */
export const ARTICLE_BODY_ATTRIBUTE = 'data-article-body';
